
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';

interface ProfileOrder {
  id: string;
  disc_code: string;
  position: number;
}

export const useProfileOrders = () => {
  const [profileOrders, setProfileOrders] = useState<ProfileOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { user } = useAuth();
  const { toast } = useToast();

  const fetchProfileOrders = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('profile_orders')
        .select('id, disc_code, position')
        .eq('user_id', user.id)
        .order('position', { ascending: true });

      if (error) throw error;

      setProfileOrders(data || []);
    } catch (error) {
      console.error('Error fetching profile orders:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  const saveProfileOrders = useCallback(async (newOrders: { discCode: string; position: number }[]) => {
    if (!user) return;

    setSaving(true);
    try {
      // Delete existing orders for this user
      await supabase
        .from('profile_orders')
        .delete()
        .eq('user_id', user.id);

      // Insert new orders
      const ordersToInsert = newOrders.map(order => ({
        user_id: user.id,
        disc_code: order.discCode,
        position: order.position
      }));

      const { error } = await supabase
        .from('profile_orders')
        .insert(ordersToInsert);

      if (error) throw error;

      // Update local state
      setProfileOrders(ordersToInsert.map((order, index) => ({
        id: `temp-${index}`,
        disc_code: order.disc_code,
        position: order.position
      })));

      // Refresh to get actual IDs
      await fetchProfileOrders();
    } catch (error) {
      console.error('Error saving profile orders:', error);
      toast({
        title: "Error",
        description: "Failed to save profile order",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  }, [user, toast, fetchProfileOrders]);

  const resetToDefault = useCallback(async () => {
    if (!user) return;

    setSaving(true);
    try {
      await supabase
        .from('profile_orders')
        .delete()
        .eq('user_id', user.id);

      setProfileOrders([]);
    } catch (error) {
      console.error('Error resetting profile orders:', error);
      toast({
        title: "Error",
        description: "Failed to reset profile order",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  }, [user, toast]);

  useEffect(() => {
    fetchProfileOrders();
  }, [fetchProfileOrders]);

  return {
    profileOrders,
    loading,
    saving,
    saveProfileOrders,
    resetToDefault
  };
};
